/**
 * @class
 * @extends Netx.Model
 */
import Base from '@netx/core/lib/netx-model';

// import CMSBlockModel from './CMSBlock';
import CMSBlocksCollection from '../collections/CMSBlocks';
import moment from '@netx/core/lib/netx-moment';

export default class CMSPage extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}                      defaults              - The default attribute values
	 * @property {string}                      defaults.portalId     - Unique ID of a portal
	 * @property {string}                      defaults.slug         - mutable unique key
	 * @property {number}                      defaults.userId       - Id of owner/creator
	 * @property {string}                      defaults.modDate      - set by server on update
	 * @property {module:cms/models/CMSBlocks} defaults.blocks       - Collection of block models for this page
	 * @property {string}                      defaults.template     - page template key
	 * @property {string}                      defaults.title        - page title
	 * @property {string}                      defaults.description  - displayed in search, built from first h2
	 * @property {string}                      defaults.displayTitle  - display page title toggle
	 */
	get defaults() {
		return {
			// id: 0,
			portalId: '',
			slug: '',
			userId: 0,
			modDate: '',
			// Synthesized from result.blocks at load time by _parse() :
			blocks: new CMSBlocksCollection(),
			template: 'template1',
			title: '',
			description: '',
			displayTitle: false,
		};
	}

	/**
	 * attrs to convert to/from 'goo'
	 * @property {array} clientArrs
	 */
	get gooAttrs() {
		return ['template', 'title', 'displayTitle'];
	}

	/**
	 * attrs to hide from the server
	 * @property {array} clientArrs
	 */
	get clientAttrs() {
		return ['blocks'];
	}

	/**
	 * RPC methods:
	 * @rpcs
	 */
	get methods() {
		return {
			getPageForId: ['getPageForId', 'id'],
			getPageForPortal: ['getPageForPortal', 'portalId', 'slug'],
			read: function () {
				if (this.id) {
					return ['getPageForId', this.id];
				} else if (this.get('portalId') && this.get('slug')) {
					return ['getPageForPortal', this.get('portalId'), this.get('slug')];
				} else {
					throw new Error("can't fetch page without ID or portal+slug");
				}
			},
			/**
			 * @method fetch
			 */
			create: function () {
				if (!this.get('title')) {
					return new $.Deferred().reject();
				}

				if (!this.get('slug')) {
					this.set({
						slug: this.makeSlug(this.get('title')),
					});
				}
				return ['setPage', this.self()];
			},

			update: [
				'setPage',
				function () {
					return this.self();
				},
			],
			/** @method destroy */
			remove: ['deletePage', 'id'],
		};
	}

	/**
	 * TODO: DAM-8469 & unwrap
	 * @method
	 * @param {int} id
	 */
	fetchById(id) {
		this.set({id: id});
		return this.fetch();
	}

	/**
	 * TODO: DAM-8469 & unwrap
	 * @method
	 * @param {int} portalId
	 * @param {string} slug
	 */
	fetchByPortalSlug(portalId, slug) {
		this.set({
			portalId: portalId,
			slug: slug,
		});
		return this.getPageForPortal.apply(this, _.rest(arguments));
	}

	/** @member */
	get parsers() {
		return {
			getPageForId: function (result) {
				this._parse(result, {parse: true});
			},
			getPageForPortal: function (result) {
				this._parse(result);
			},
			create: function (result) {
				if (result && result > 0) {
					this.set({
						pageId: result,
						id: result,
					});
				}
			},
		};
	}

	/**
	 * multipurpose backbone parse method.
	 * @param {object} result the server's response
	 * @param {object} opts the sync options
	 * @return {object} the parsed data
	 * @method
	 */
	parse(result, opts) {
		if (_.isNumeric(result)) {
			// got just an ID
			return {id: result};
		} else {
			// got a whole object

			// DAM-19186 - if old page, assume show unless home
			if (result.displayTitle === undefined) {
				result.displayTitle = result.slug !== 'home';
			}
			this._parse(result, opts);
		}
		return this.attributes;
	}

	/**
	 * Convert results array: create block objects for page.
	 * @method
	 */
	_parse(page, opts) {
		var attributes = this.unpackAttrs(page);
		// generic version?
		this.set(_.omit(attributes, 'blocks'), _.omit(opts, 'parse'));
		this.setModDate();
		this.setBlocks(attributes.blocks, opts);
	}

	/**
	 * Convert blocks & blocks.arrays to bare objects when syncing
	 * @override
	 */
	self() {
		const me = super.self(),
			blocksCollection =
				this.get('blocks') || this.set({blocks: new CMSBlocksCollection()});

		me.blocks = _.map(blocksCollection.models, function (block, idx, all) {
			return block.self();
		});

		return me;
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		if (this.get('goo')) {
			var gooAttrs = this.unpackAttrs(
				_.pick(this.attributes, this.gooAttrs, 'goo'),
			);
			this.set(gooAttrs);
		}
		// for automatic page saves
		this.debouncedSave = _.debounce(this.debouncedSave, 1000);
	}

	/** @override */
	save() {
		return this.update();
	}
	/**
	 * @method
	 * Pages get saved automatically on various changes
	 * thus debouncing is necessary, like when creating a new block
	 */
	debouncedSave() {
		return this.update();
	}

	/**
	 * Utility method: makeSlug() converts its argument into a more URL-y string.
	 * If this page model has a collection property, the models in that collection
	 * are checked to avoid duplicate slugs.
	 * @method
	 * @param {string} name
	 */
	makeSlug(name) {
		// Based on:
		// http://stackoverflow.com/questions/1053902/how-to-convert-a-title-to-a-url-slug-in-jquery
		var suggestion = name
			.toLowerCase() // make everything lowercase
			.replace(/[-\W]+/g, '-') // convert pattersn of multiple dashes or non-word characters -> a single dash
			.replace(/^-/, '') // strip any dash from front of slug
			.replace(/-$/, ''); // strip any dash from end of slug

		// If we have a collection, search it for duplicate slugs.
		if (!this.isUniqueSlug(suggestion)) {
			var index = 2;
			// Keep incrementing & searching until we can't find this modified slug in our collection.
			while (!this.isUniqueSlug(suggestion + '-' + index)) {
				index++;
			}
			suggestion += '-' + index;
		}

		return suggestion;
	}

	/**
	 * Utility method to test whether a page slug is unique within this.collection.
	 * (If this.collection is undefined, this method returns true.)
	 * @method
	 */
	isUniqueSlug(slug) {
		const collection = this.collection || Netx.cms.portal.pages;

		if (!collection) {
			return true;
		}

		// If slug is undefined, use this model's property
		slug = slug || this.get('slug');

		return !collection.find(function (m) {
			// Is there a model other than this one with the same slug already?
			// (We specifically exclude our own model's ID.)
			return m.id != this.id && m.get('slug') == slug.trim();
		}, this);
	}

	/**
	 * Set our blocks property, converting non-models into models & fetching them as needed.
	 * @method
	 */
	setBlocks(blocks, opts) {
		if (!blocks || blocks.length == 0) {
			// create new block, with id and everything
			dispatcher.trigger('cms:page:block:add', this);
		}

		var blockCollection = _.toCollection(blocks, CMSBlocksCollection, {
			parse: true,
		});

		if (this.get('blocks') instanceof CMSBlocksCollection) {
			// Update existing blocks collection
			this.get('blocks').reset(blockCollection.models, {parse: true});
		} else {
			// Attach new blocks collection
			this.set({blocks: blockCollection}, {parse: true});
		}
	}

	/** @method */
	parseBlocks() {
		if (this.parsed) return;

		const blocksCollection = this.get('blocks');
		blocksCollection.each((block, idx, all) => {
			block._parse(block.attributes, {parse: true});
		});
		this.parsed = true;
	}

	/**
	 * @method
	 */
	setModDate() {
		this.set({
			modDate: moment(this.get('modDate')).format('MMMM Do, YYYY (h:mm:ss a)'),
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSPage';
	}
}
